import { useEffect, useMemo, useState, type HTMLAttributes } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bars3Icon } from '@heroicons/react/24/outline';
import { StarIcon } from '@heroicons/react/24/solid';
import { DragDropContext, Draggable, Droppable, DropResult } from '@hello-pangea/dnd';
import { useTranslation } from '@/store/languageStore';
import { ROUTES } from '@/constants/routes';
import { FAVORITE_SUB_MODULES } from '@/constants/favoriteSubModules';
import { getUserMenu } from '@/services/systems/systemsService';
import type { MenuModule, MenuSystem } from '@/types/systems';
import { withBackTo } from '@/utils/navigationBack';
import { getIconColors, getModuleIcon } from '@/utils/moduleIcons';
import { favoriteModuleKey, tModule } from '@/utils/moduleLabels';
import { resolveWorkshopModuleRoute } from '@/utils/workshopModuleRoutes';
import { useFavoriteModules } from '@/hooks/useFavoriteModules';

interface FavoriteEntry {
  key: string;
  title: string;
  icon?: string;
  route?: string;
}

interface FavoriteModulesBarProps {
  editMode?: boolean;
}

export default function FavoriteModulesBar({ editMode = false }: FavoriteModulesBarProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { favoriteKeys, reorderFavorites, loading: favoritesLoading } = useFavoriteModules();
  const [systems, setSystems] = useState<MenuSystem[]>([]);

  useEffect(() => {
    let cancelled = false;
    getUserMenu()
      .then((menu) => {
        if (!cancelled) setSystems(menu.systems || []);
      })
      .catch(() => {
        if (!cancelled) setSystems([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const favorites = useMemo(() => {
    const byKey = new Map<string, FavoriteEntry>();
    for (const system of systems) {
      const walk = (modules: MenuModule[]) => {
        for (const module of modules) {
          const key = favoriteModuleKey(system.name, module.name);
          byKey.set(key, {
            key,
            title: tModule(t, module.name, 'title', system.name) || module.displayName,
            icon: module.icon,
            route: resolveWorkshopModuleRoute(system.name, module.name, module.route),
          });
          if (module.children?.length) walk(module.children);
        }
      };
      walk(system.modules || []);
    }

    for (const sub of FAVORITE_SUB_MODULES) {
      const key = favoriteModuleKey(sub.systemName, sub.moduleName);
      const translated = t(sub.titleKey);
      byKey.set(key, {
        key,
        title: translated && translated !== sub.titleKey ? translated : sub.fallbackTitle,
        icon: sub.icon,
        route: sub.route,
      });
    }

    return favoriteKeys
      .map((key) => byKey.get(key))
      .filter((entry): entry is FavoriteEntry => Boolean(entry?.route));
  }, [favoriteKeys, systems, t]);

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    void reorderFavorites(result.source.index, result.destination.index);
  };

  if (favoritesLoading || favorites.length === 0) {
    return null;
  }

  const renderChip = (entry: FavoriteEntry, dragHandle?: HTMLAttributes<HTMLElement>) => {
    const colors = getIconColors(entry.icon);
    return (
      <button
        type="button"
        onClick={() => {
          if (editMode) return;
          navigate(entry.route as string, withBackTo(ROUTES.HOME));
        }}
        className="flex items-center gap-2 shrink-0 max-w-[14rem] px-3 py-1.5 rounded-lg border border-border bg-bgElevated hover:border-bgPrimary hover:shadow-sm transition-colors"
        title={entry.title}
      >
        {editMode && (
          <span
            {...dragHandle}
            className="text-textTertiary cursor-grab active:cursor-grabbing"
            aria-label={t('dashboard.reorderFavorites')}
          >
            <Bars3Icon className="w-4 h-4" />
          </span>
        )}
        <span className={`p-1 rounded-md ${colors.bgClass} ${colors.textClass}`}>
          {getModuleIcon(entry.icon, 'w-4 h-4')}
        </span>
        <span className="text-sm text-textDefault truncate">{entry.title}</span>
      </button>
    );
  };

  return (
    <div className="mb-6">
      <div className="flex items-center gap-2 mb-2 text-sm font-medium text-textSecondary">
        <StarIcon className="w-4 h-4 text-amber-400" />
        <span>{t('dashboard.favorites')}</span>
        {editMode && (
          <span className="font-normal text-textTertiary">— {t('dashboard.reorderFavoritesHint')}</span>
        )}
      </div>
      {editMode ? (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="favorite-modules" direction="horizontal">
            {(provided) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className="flex items-center gap-2 overflow-x-auto pb-1"
              >
                {favorites.map((entry, index) => (
                  <Draggable key={entry.key} draggableId={entry.key} index={index}>
                    {(drag, snapshot) => (
                      <div
                        ref={drag.innerRef}
                        {...drag.draggableProps}
                        className={snapshot.isDragging ? 'z-50' : undefined}
                      >
                        {renderChip(entry, drag.dragHandleProps ?? undefined)}
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      ) : (
        <div className="flex items-center gap-2 overflow-x-auto pb-1">
          {favorites.map((entry) => (
            <div key={entry.key}>{renderChip(entry)}</div>
          ))}
        </div>
      )}
    </div>
  );
}
